import { useRewardLogic } from '@/hooks/useRewardLogic';
import { RewardForm } from '@/components/company/reward/RewardForm';
import FormModal from '@/components/company/FormModal';
import { RewardFilters } from '@/components/company/reward/RewardFilters';
import { rewardColumns } from '@/components/company/reward/rewardColumns';
import CompanyHeader from '@/components/company/CompanyHeader';
import MainTable from '@/components/company/table/MainTable';
import TableAddButton from '@/components/company/TableAddButton';

import { useIsReadOnly } from '@/utils/auth/roleChecker';
import useCompanyState from '@/states/company.state';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import VideoPlayer from '@/components/VideoPlayer';

export default function Reward() {
  const isReadOnly = useIsReadOnly();
  const {
    isLoading,
    allPublished,
    paginatedData,
    formControl,
    formMode,
    formRef,
    fields,
    handleSave,
    handleDelete,
    onEdit,
    reset,
    pageConfig,
    setPageConfig,
    bulkPublishRewardHandler,
    searchTerm,
    setSearchTerm,
    clearFilters,
    setFormMode,
  } = useRewardLogic();

  const handleCancel = () => {
    reset({
      bonus_name: '',
      bonus_period: '',
      bonus_target: '',
      bonus_amount: '',
      is_published: false,
      category: '',
    });
    setFormMode(null);
  };

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const rewardStep = companyProfileSteps.find(step => step.step_name.trim().toLowerCase() === 'reward');
  const videoLink = rewardStep?.video_link;
  const tips = rewardStep?.tips;

  // Data now comes from server-side pagination
  const displayData = paginatedData || {
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 10,
    number: 0,
  };

  return (
    <section className="w-full">
      <CompanyHeader
        title="Reward"
        description="Anda akan merancang Peraturan Perusahaan, Tata Tertib, Skema Bonus, Skema Denda/Hukuman, dan Surat Peringatan."
        extraAction={videoLink ? <VideoPlayer url={videoLink} tipsHtml={tips} /> : undefined}
        onPublish={isReadOnly ? undefined : bulkPublishRewardHandler}
        isPublished={allPublished}
      />

      <div className="mx-5 border-b border-gray-200 dark:border-gray-700" />

      <FormModal
        isOpen={formMode !== null}
        title={formMode === 'edit' ? 'Edit Reward' : 'Buat Reward'}
        onClose={handleCancel}
        onSubmit={formControl.handleSubmit(handleSave)}
        submitLabel={formMode === 'edit' ? 'Perbarui' : 'Simpan'}
        isSaving={isLoading}
        panelClassName="w-full max-w-2xl"
      >
        <RewardForm
          formControl={formControl}
          formRef={formRef}
          fields={fields}
          handleSave={handleSave}
          disabled={isReadOnly}
        />
      </FormModal>

      <div className="mx-5 my-5 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-[#1A1A1A]">
        <RewardFilters
          searchTerm={searchTerm}
          setSearchTerm={(val: string) => {
            setSearchTerm(val);
            setPageConfig(prev => ({ ...prev, page: 0 }));
          }}
          clearFilters={clearFilters}
        />

        <MainTable
          showHeader={false}
          isLoading={isLoading}
          data={{
            content: displayData.content as unknown as { [key: string]: unknown }[],
            totalElements: displayData.totalElements,
            totalPages: displayData.totalPages,
          }}
          columns={rewardColumns}
          pageConfig={pageConfig}
          setPageConfig={setPageConfig}
          onEdit={onEdit}
          onDelete={handleDelete}
          showActions={!isReadOnly}
          useConfirmDelete
          sectionClassName="px-5 !bg-white dark:!bg-[#1A1A1A]"
        />

        {!isReadOnly && (
          <TableAddButton
            onClick={() => {
              reset({
                bonus_name: '',
                bonus_period: '',
                bonus_target: '',
                bonus_amount: '',
                is_published: false,
              });
              setFormMode('add');
            }}
          />
        )}
      </div>
    </section>
  );
}
